import {
  Button, flattenErrors, flattenErrorTypeArray, Flex, FlexColumn, FlexGrowCss, Modal,
  SecondaryButton,
  SuccessInfo,
  useInterval,
  useStickySafeNavigate,
  useStyledTheme,
} from '@equifinance/shared';
import styled, { css, keyframes } from 'styled-components';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { Search, SearchStatuses, SearchTypes } from '../../../types/Search';
import useSearchApi from '../../../api/useSearchApi';
import { useAccountContext } from '../../../contexts';
import {
  CreditSearchCrossIcon, CreditSearchNAIcon, CreditSearchStrokeIcon, CreditSearchTickIcon,
} from './CreditSearchIcons';

const Container = styled.div`${({ theme }) => `
  padding: ${theme.padding[6]};
`}`;

const InfoText = styled.div`${({ theme }) => `
  font-weight: ${theme.fontWeight.bold};
  padding-bottom: ${theme.padding[6]};
`}`;

const ButtonContainer = styled(Flex)`${({ theme }) => `
  justify-content: end;
  gap: ${theme.padding[4]};
`}`;

const NextButtonContainer = styled(ButtonContainer)`${({ theme }) => `
  padding-top:${theme.padding[6]};
`}`;

export const Break = styled.div` ${({ theme }) => `
  height:2px;
  background:${theme.colours.neutral[8]};
  margin-top:${theme.padding[6]};
  margin-bottom:${theme.padding[6]};
`}`;

const SearchIcon = styled.div` ${({ theme }) => `
  width: 0;
  margin-top: ${theme.padding[9]};
`}`;

const SearchOuterContainer = styled(Flex)` ${({ theme }) => `
  gap: ${theme.padding[4]};
`}`;

const SearchContainer = styled.div`${({ theme }) => `
  ${FlexGrowCss}
  border-radius: ${theme.borderRadius[2]};
  background:blue;
  background: ${theme.colours.neutral[10]};
`}`;

const SearchInnerContainer = styled(FlexColumn)`${({ theme }) => `
  padding:${theme.padding[6]};
`}`;

const SearchTitle = styled(Flex) <{ queued: boolean }>`${({ theme, queued }) => `
  color: ${theme.colours.neutral[queued ? 5 : 2]};
  font-weight: ${theme.fontWeight.bold};
`}`;

const ProgressTitle = styled(Flex)`${({ theme }) => `
  justify-content: end;
  font-size: ${theme.fontSize[1]};
  color: ${theme.colours.neutral[2]};
`}`;

const ProgressBarContainer = styled.div`${({ theme }) => `
  width:100%;
  background: ${theme.colours.neutral[8]};
  height: 4px;
  border-radius: 2px;
  position: relative;
  overflow: hidden;
`}`;

const animationRule = css(
  (['', ' 3s linear infinite;'] as any) as TemplateStringsArray,
  keyframes`  
    0%{
      left: -100%;
    }
    100%{
      left: 100%;
    }
  `,
);

const ProgressBarPercent = styled.div<{ status: SearchStatuses }>`
  ${({ theme, status }) => `
    background: ${status === SearchStatuses.IN_PROGRESS ? theme.colours.secondary[1] : theme.colours.primary[1]};
    height: 100%;
    width: ${status === SearchStatuses.COMPLETED ? '100%' : '0'};
    border-radius: 2px;
`}`;

const ProgressBarPercentLoading = styled(ProgressBarPercent) <{ status: SearchStatuses }>`
  animation: ${animationRule};
  position: absolute;
  left: -100%;
  width: 100%;
`;

const SearchInfoContainer = styled(FlexColumn)`${({ theme }) => `
  padding-left: ${theme.padding[9]};
  padding-right: ${theme.padding[9]};
  gap: ${theme.padding[7]};
`}`;

const SearchInfoMessage = styled.div<{ isError?: boolean }>`${({ theme, isError = false }) => `
  border-bottom-left-radius: ${theme.borderRadius[2]};
  border-bottom-right-radius: ${theme.borderRadius[2]};
  background: ${isError ? theme.colours.danger[2] : theme.colours.warning[2]};
  padding: ${theme.padding[6]};
  padding-top: ${theme.padding[2]};
  padding-bottom: ${theme.padding[2]};
`}`;

function ProgressBar({ status }: { status: SearchStatuses }) {
  return (
    <ProgressBarContainer>
      {status === SearchStatuses.IN_PROGRESS
        ? <ProgressBarPercentLoading status={status} />
        : <ProgressBarPercent status={status} />}
    </ProgressBarContainer>
  );
}

function SearchInfo({
  title,
  status = SearchStatuses.QUEUED,
  message = '',
  isErrorMessage = false,
}: {
  title: string,
  status?: SearchStatuses,
  message?: string,
  isErrorMessage?: boolean,
}) {
  const getProgressTitle = () => {
    switch (status) {
      case SearchStatuses.QUEUED:
        return 'Queued';
      case SearchStatuses.FAILED:
        return 'Failed';
      case SearchStatuses.COMPLETED:
        return 'Completed';
      case SearchStatuses.IN_PROGRESS:
        return 'In progress';
      case SearchStatuses.NOT_APPLICABLE:
        return 'Not applicable';
      case SearchStatuses.NO_DATA:
        return 'No data';
      default:
        return '';
    }
  };

  const progressTitle = getProgressTitle();

  return (
    <SearchOuterContainer>
      <SearchContainer>
        <SearchInnerContainer>
          <SearchTitle queued={!status || status === SearchStatuses.QUEUED}>{title}</SearchTitle>
          <ProgressTitle>{progressTitle}</ProgressTitle>
          <ProgressBar status={status} />
        </SearchInnerContainer>
        {!!message && (
          <SearchInfoMessage isError={isErrorMessage}>{message}</SearchInfoMessage>
        )}
      </SearchContainer>

      <SearchIcon>
        {status === SearchStatuses.COMPLETED && (
          <CreditSearchTickIcon />
        )}
        {status === SearchStatuses.FAILED && (
          <CreditSearchCrossIcon />
        )}
        {status === SearchStatuses.NO_DATA && (
          <CreditSearchStrokeIcon />
        )}
        {status === SearchStatuses.NOT_APPLICABLE && (
          <CreditSearchNAIcon />
        )}
      </SearchIcon>

    </SearchOuterContainer>

  );
}

function CreditSearchInfo({ search }: { search?: Search }) {
  const getSearchMessage = () => {
    switch (search?.status) {
      case SearchStatuses.NO_DATA:
        return {
          message: 'No data found. Please go back and make sure that all Applicant\'s information has been entered correctly.',
        };
      case SearchStatuses.FAILED:
        return {
          message: 'The search has failed. Try again later.',
          isError: true,
        };
      default:
        return {
          message: '',
        };
    }
  };

  const searchMessage = getSearchMessage();

  return (
    <SearchInfo
      title="Credit search"
      status={search?.status}
      message={searchMessage?.message}
      isErrorMessage={searchMessage?.isError}
    />
  );
}

function AVMSearchInfo({ search }: { search?: Search }) {
  const getSearchMessage = () => {
    switch (search?.status) {
      case SearchStatuses.NOT_APPLICABLE:
        return {
          message: 'AVM search is not applicable as it doesn\'t match criteria. Evidence will have to be provided before the submission of the application.',
        };
      case SearchStatuses.NO_DATA:
        return {
          message: 'No data found. An alternative valuation method will be required.',
        };
      case SearchStatuses.FAILED:
        return {
          message: 'There has been a technical issue. AVM search cannot be performed.',
          isError: true,
        };
      default:
        return {
          message: '',
        };
    }
  };

  const searchMessage = getSearchMessage();

  return (
    <SearchInfo
      title="AVM search"
      status={search?.status}
      message={searchMessage?.message}
      isErrorMessage={searchMessage?.isError}
    />
  );
}

export function CreditSearchModal({ isOpen, closeModal }: { isOpen: boolean, closeModal: () => void }) {
  const searchesCallDelay = 1000;

  const {
    id, account, updateAccount,
  } = useAccountContext();

  const { getSearches } = useSearchApi();

  const [showInfoText, setShowInfoText] = useState<boolean>(true);

  const [callSearchesApi, setCallSearchesApi] = useState<boolean>(false);
  const [creditSearch, setCreditSearch] = useState<Search>();
  const [AVMSearch, setAVMSearch] = useState<Search>();
  const [applicationCreated, setApplicationCreated] = useState<boolean>(false);

  const callSearches = async () => {
    const searches = await getSearches(id);

    setCreditSearch(searches?.find((search) => search?.type === SearchTypes.Credit));
    setAVMSearch(searches?.find((search) => search?.type === SearchTypes.AVM));

    if (searches.every((search) => search?.status !== null && ![SearchStatuses.IN_PROGRESS, SearchStatuses.QUEUED].includes(search?.status))) {
      setCallSearchesApi(false);
    }
  };

  useInterval(() => {
    if (callSearchesApi) {
      callSearches();
    }
  }, searchesCallDelay);

  const consentToSearchUpdated = useMemo(() => {
    const errors = flattenErrors(account.errors);
    return !!account.consentToSearch && !errors.find(
      (error) => error.propertyName === 'consentToSearch' && error.errorType === 'ValidationFailure',
    );
  }, [account.errors, account.consentToSearch]);

  const searchInProgress = !creditSearch?.status
    || creditSearch?.status === SearchStatuses.QUEUED
    || creditSearch?.status === SearchStatuses.IN_PROGRESS
    || !AVMSearch?.status
    || AVMSearch?.status === SearchStatuses.QUEUED
    || AVMSearch?.status === SearchStatuses.IN_PROGRESS;

  useEffect(() => {
    if (consentToSearchUpdated) {
      const errors = flattenErrorTypeArray(account.errors?.[id] ?? []);
      const warnings = flattenErrorTypeArray(account.warnings?.[id] ?? []);

      const creditSearchFailed = errors.find(
        (error) => error.propertyName === 'creditSearch' && error.errorCode === 'CreditSearchFailed',
      );

      if (creditSearchFailed) {
        setCreditSearch({
          type: SearchTypes.Credit,
          status: SearchStatuses.FAILED,
          errorMessage: creditSearchFailed.errorMessage,
        });
      }

      const creditSearchNoData = warnings.find(
        (error) => error.propertyName === 'creditSearch' && error.errorCode === 'CreditSearchNoData',
      );

      if (creditSearchNoData) {
        setCreditSearch({
          type: SearchTypes.Credit,
          status: SearchStatuses.NO_DATA,
          errorMessage: creditSearchNoData.errorMessage,
        });
      }

      setApplicationCreated(consentToSearchUpdated && !creditSearchFailed && !creditSearchNoData);
    }
  }, [id, account.errors, account.warnings, consentToSearchUpdated]);

  useEffect(() => {
    if (applicationCreated && !callSearchesApi) {
      setCallSearchesApi(true);
    }
  }, [applicationCreated]);

  const onClickStartSearches = async () => {
    setShowInfoText(false);
    updateAccount('consentToSearch', moment().toISOString());
  };

  const navigate = useStickySafeNavigate();

  const onClickNext = () => {
    navigate(`/products/${id}`);
  };

  const close = () => {
    closeModal();
    setCallSearchesApi(false);
  };

  const theme = useStyledTheme();

  return (
    <Modal
      title="Searches"
      isOpen={isOpen}
      hideModal={close}
      cssChildren={`background: ${theme.colours.neutral[9]};`}
      hideClose
    >
      <Container>
        {showInfoText && (
          <>
            <InfoText>
              We are about to perform a soft credit search; you confirm that you hold the applicant(s) authority to carry this out.
              <br />
              During this process we will also undertake an AVM.
            </InfoText>

            <ButtonContainer>
              <SecondaryButton onClick={close}>Back</SecondaryButton>
              <Button onClick={onClickStartSearches}>Start searches</Button>
            </ButtonContainer>
            <Break />
          </>
        )}
        <SearchInfoContainer>
          <CreditSearchInfo search={creditSearch} />
          <AVMSearchInfo search={AVMSearch} />

          {(creditSearch?.status === SearchStatuses.COMPLETED && AVMSearch?.status === SearchStatuses.COMPLETED)
            && <SuccessInfo>Both searches have been successfully completed.</SuccessInfo>}

        </SearchInfoContainer>

        <NextButtonContainer>
          {(!creditSearch?.status || (creditSearch?.status !== SearchStatuses.NO_DATA && creditSearch?.status !== SearchStatuses.FAILED))
            ? (
              <Button
                onClick={onClickNext}
                disabled={searchInProgress}
              >
                Next
              </Button>
            ) : (
              <Button
                onClick={close}
              >
                Back
              </Button>
            )}
        </NextButtonContainer>
      </Container>
    </Modal>
  );
}
